import { Component, OnInit, signal } from "@angular/core";
import { CommonModule, DatePipe } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { SpinnerLoaderComponent } from "../../../shared/components/spinner-loader/spinner-loader.component";
import { SearchBoxComponent } from "../../../shared/components/search-box/search-box.component";
import { finalize } from "rxjs";
import { LogDetailsModalComponent } from "./log-details-modal/log-details-modal.component";
import { SelectModule } from "primeng/select";
import { DatePicker, DatePickerModule } from "primeng/datepicker";
import { LoggerService } from "../../../../Utils/_services/logger/logger.service";
import { ILogApiItem, ILogRequest, LogLevel } from "../../../../Utils/_services/logger/logger.model";


@Component({
  selector: "app-logger",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DatePickerModule,
    SpinnerLoaderComponent,
    SelectModule,
    SearchBoxComponent,
    DatePipe,
    LogDetailsModalComponent,
  ],
  templateUrl: "./logger.component.html",
  styleUrl: "./logger.component.css",
})
export class LoggerComponent implements OnInit {
  constructor(private loggerService: LoggerService) {}

  logs = signal<ILogApiItem[]>([]);
  allSelected: boolean = false;
  currentPage: number = 1;
  itemsPerPage: number = 3;
  totalEntries = signal(0);
  totalPages: number = 0;
  startIndex: number = 1;
  endIndex: number = 10;
  pageSizes: number[] = [5, 10, 20, 50];
  searchTerm: string | null = null;
  rangeDates: Date[] | undefined;
  loader = signal(false);
  logLevels: { label: string; value: string }[] = [];
  selectedLogLevel: LogLevel | null = null;
  selectedLog = signal<ILogApiItem | null>(null);
  isView = signal<boolean>(false);
  showDeleteBtn = signal(false);
  currentSortField = signal<
    "LevelName" | "Timestamp" |"Application" | null
  >(null);
  currentSortOrder = signal<"asc" | "desc" | null>(null);

  ngOnInit(): void {
    this.itemsPerPage = this.pageSizes[0];
    this.logLevels = Object.values(LogLevel).map(l => ({
      label: l,
      value: l,
    }));
    this.loadData();
  }

  onSearch(value: string | null) {
    this.currentPage = 1;
    this.searchTerm = value;
    this.loadData();
  }

  onDateFilter() {
    if (!this.rangeDates?.[0] || !this.rangeDates?.[1]) return;
    this.loadData();
  }

  formatLocalDate = (d: Date) =>
    `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;

  loadData() {
    this.loader.set(true);
    const req: ILogRequest = {
      type: this.selectedLogLevel,
      pageNumber: this.currentPage,
      pageSize: this.itemsPerPage,
      startDate: !this.rangeDates?.[0]
        ? null
        : this.formatLocalDate(this.rangeDates[0]),
      endDate: !this.rangeDates?.[1]
        ? null
        : this.formatLocalDate(this.rangeDates[1]),
      search: this.searchTerm,
      sort: this.currentSortField(),
      sortOrder: this.currentSortField() ? this.currentSortOrder() : null
    };

    this.loggerService
      .getAllLogs(req)
      .pipe(finalize(() => this.loader.set(false)))
      .subscribe({
        next: data => {
          this.logs.set(data.data.logs);
          this.totalEntries.set(data.data.totalCount);
          this.updatePagination();
        },
        error: err => {
          console.error("Error fetching logs:", err);
        },
      });
  }

  onSort(
    field: "LevelName" | "Timestamp" |"Application"
  ): void {
    if (this.currentSortField() === field) {
      if (this.currentSortOrder() === "asc") {
        this.currentSortOrder.set("desc");
      } else if (this.currentSortOrder() === "desc") {
        this.currentSortOrder.set(null);
        this.currentSortField.set(null);
      }
    } else {
      this.currentSortField.set(field);
      this.currentSortOrder.set("asc");
    }
    this.loadData();
  }

  getSortIcon(field: string): string {
    if (this.currentSortField() !== field) return "⇅";
    if (this.currentSortOrder() === "asc") return "↑";
    if (this.currentSortOrder() === "desc") return "↓";
    return "⇅";
  }

  updateSelectAllState() {
    const selectedCount = this.logs().filter(log => log.selected).length;
    this.allSelected =
      selectedCount === this.logs().length && this.logs().length > 0;
    this.showDeleteBtn.set(this.logs().filter(x => x.selected).length > 0);
  }

  toggleSelectAll() {
    this.allSelected = !this.allSelected;
    this.logs().forEach(x => (x.selected = this.allSelected));
    this.showDeleteBtn.set(this.logs().filter(x => x.selected).length > 0);
  }

  deleteLogs() {
    this.loggerService
      .deleteLogs(
        this.logs()
          .filter(x => x.selected)
          .map(x => x._id)
      )
      .subscribe({
        next: res => {
          this.loadData();
        },
      });
  }

  onPageSizeChange(newSize: string) {
    this.itemsPerPage = +newSize;
    this.currentPage = 1;
    this.updatePagination();
    this.loadData();
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePagination();
      this.loadData();
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePagination();
      this.loadData();
    }
  }

  updatePagination() {
    this.totalPages = Math.ceil(this.totalEntries() / this.itemsPerPage);
    this.startIndex = (this.currentPage - 1) * this.itemsPerPage + 1;
    this.endIndex = Math.min(
      this.currentPage * this.itemsPerPage,
      this.totalEntries()
    );
    this.updateSelectAllState();
  }

  viewDetails(log: ILogApiItem) {
    this.selectedLog.set(log);
    this.isView.set(true);
  }

  closeViewDetails() {
    this.selectedLog.set(null);
    this.isView.set(false);
  }
}
